import React, { useState, useEffect } from 'react';
import {
  Users,
  Wrench,
  Bot,
  Building2,
  CheckCircle,
  LifeBuoy,
  Database,
  Activity,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import adminAnalyticsService from '../services/adminAnalyticsService';

const PIE_COLORS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#06b6d4', '#84cc16',
];

const fmt = (n) =>
  n !== undefined && n !== null ? Number(n).toLocaleString() : '—';

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-3xl font-bold text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const AnalyticsDashboard = () => {
  const [adminStats, setAdminStats] = useState(null);
  const [subStats, setSubStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      adminAnalyticsService.getAdminStats(),
      adminAnalyticsService.getSubscriptionStats(),
    ]).then(([adminRes, subRes]) => {
      if (adminRes.status === 'fulfilled') setAdminStats(adminRes.value);
      else console.error('Failed to load admin stats:', adminRes.reason);
      if (subRes.status === 'fulfilled') setSubStats(subRes.value);
      else console.error('Failed to load subscription stats:', subRes.reason);
    }).finally(() => setLoading(false));
  }, []);

  // ── Subscription status grouped bar chart data ───────────────────────────
  const subscriptionStatusData = subStats
    ? [
        {
          service: 'CMMS',
          Active: subStats.cmmsActive ?? 0,
          Trial: subStats.cmmsTrial ?? 0,
          Cancelled: subStats.cmmsCancelled ?? 0,
        },
        {
          service: 'Chatbot',
          Active: subStats.chatbotActive ?? 0,
          Trial: subStats.chatbotTrial ?? 0,
          Cancelled: subStats.chatbotCancelled ?? 0,
        },
      ]
    : [];

  // ── Plan distribution pie chart data ────────────────────────────────────
  const planDistData = subStats
    ? [
        ...(subStats.cmmsPlans || []).map((p) => ({
          name: `CMMS: ${p.planName}`,
          value: Number(p.count),
        })),
        ...(subStats.chatbotPlans || []).map((p) => ({
          name: `Bot: ${p.planName}`,
          value: Number(p.count),
        })),
      ]
    : [];

  const noPlans = planDistData.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Platform Analytics</h1>
        <p className="text-gray-600 mt-1">Real-time insights from platform data</p>
      </div>

      {/* ── Section 1: Platform Stats ────────────────────────────────────── */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={Building2}
            label="Total Companies"
            value={fmt(adminStats?.totalCompanies)}
            color="bg-purple-500"
          />
          <StatCard
            icon={CheckCircle}
            label="Active Companies"
            value={fmt(adminStats?.activeCompanies)}
            color="bg-green-500"
          />
          <StatCard
            icon={Users}
            label="Platform Admin Users"
            value={fmt(adminStats?.totalPlatformUsers)}
            color="bg-blue-500"
          />
          <StatCard
            icon={LifeBuoy}
            label="Open Support Tickets"
            value={fmt(adminStats?.openSupportTickets)}
            color="bg-orange-500"
          />
        </div>
      )}

      {/* ── Section 2: Subscription Overview ────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription status bar chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            Subscription Status by Service
          </h2>
          <p className="text-xs text-gray-400 mb-6">
            Live counts from subscription database
          </p>
          {subscriptionStatusData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              No subscription data
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={subscriptionStatusData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="service" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Active" fill="#10b981" name="Active" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Trial" fill="#3b82f6" name="Trial" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Cancelled" fill="#9ca3af" name="Cancelled" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Plan distribution pie chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Plan Distribution</h2>
          <p className="text-xs text-gray-400 mb-4">
            Active subscriptions by plan across all services
          </p>
          {noPlans ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              No plan subscriptions yet
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={planDistData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {planDistData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Subscriptions']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-1 gap-2 mt-3">
                {planDistData.map((plan, index) => (
                  <div
                    key={plan.name}
                    className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                      />
                      <span className="text-sm text-gray-700">{plan.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {plan.value}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Section 3: Chatbot Usage ─────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-1">
          <Bot className="w-5 h-5 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-900">Chatbot Usage</h2>
        </div>
        <p className="text-xs text-gray-400 mb-6">
          Aggregate totals across all company chatbot subscriptions.
          Per-company analytics are available in the Chatbots section.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
            <div className="text-sm text-purple-600 font-medium">Active Subscriptions</div>
            <div className="text-3xl font-bold text-purple-700 mt-2">
              {loading ? '…' : fmt(subStats?.chatbotActive)}
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
            <div className="text-sm text-blue-600 font-medium">Trial Subscriptions</div>
            <div className="text-3xl font-bold text-blue-700 mt-2">
              {loading ? '…' : fmt(subStats?.chatbotTrial)}
            </div>
          </div>
          <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
            <div className="text-sm text-amber-600 font-medium">Messages This Month</div>
            <div className="text-3xl font-bold text-amber-700 mt-2">
              {loading ? '…' : fmt(subStats?.totalChatbotMessagesThisMonth)}
            </div>
            <div className="text-xs text-amber-500 mt-1">Across all companies</div>
          </div>
        </div>
      </div>

      {/* ── Section 4: CMMS Platform Activity ───────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-1">
          <Wrench className="w-5 h-5 text-orange-600" />
          <h2 className="text-xl font-bold text-gray-900">CMMS Platform Activity</h2>
        </div>
        <p className="text-xs text-gray-400 mb-6">
          Aggregate totals across all company CMMS subscriptions.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-orange-50 rounded-xl p-5 border border-orange-100">
            <div className="text-sm text-orange-600 font-medium">Active Subscriptions</div>
            <div className="text-3xl font-bold text-orange-700 mt-2">
              {loading ? '…' : fmt(subStats?.cmmsActive)}
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
            <div className="text-sm text-blue-600 font-medium">Trial Subscriptions</div>
            <div className="text-3xl font-bold text-blue-700 mt-2">
              {loading ? '…' : fmt(subStats?.cmmsTrial)}
            </div>
          </div>
          <div className="bg-green-50 rounded-xl p-5 border border-green-100">
            <div className="flex items-center gap-1 text-sm text-green-600 font-medium">
              <Database className="w-4 h-4" />Assets Tracked
            </div>
            <div className="text-3xl font-bold text-green-700 mt-2">
              {loading ? '…' : fmt(subStats?.totalCmmsAssets)}
            </div>
            <div className="text-xs text-green-500 mt-1">Across all companies</div>
          </div>
          <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100">
            <div className="flex items-center gap-1 text-sm text-indigo-600 font-medium">
              <Activity className="w-4 h-4" />Users Managed
            </div>
            <div className="text-3xl font-bold text-indigo-700 mt-2">
              {loading ? '…' : fmt(subStats?.totalCmmsUsers)}
            </div>
            <div className="text-xs text-indigo-500 mt-1">Across all companies</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
